/**
 * Competitor Query Service
 *
 * Uses PostGIS to efficiently query gas stations within a specified radius
 */

import { supabase } from '../lib/supabase';
import { AddressRecord } from '../lib/supabaseClient';

export interface CompetitorQueryOptions {
  latitude: number;
  longitude: number;
  radiusMiles?: number;
  excludeObjectId?: number; // Exclude the subject property from results
}

export interface CompetitorWithDistance extends AddressRecord {
  distance_miles?: number;
}

/**
 * Calculate distance between two points using Haversine formula
 * Returns distance in miles
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) *
    Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return distance;
}

/**
 * Fetch competitors within a specified radius using PostGIS
 * Falls back to client-side filtering if PostGIS is not available
 */
export async function fetchCompetitorsInRadius(
  options: CompetitorQueryOptions
): Promise<CompetitorWithDistance[]> {
  const { latitude, longitude, radiusMiles = 1, excludeObjectId } = options;

  try {
    // First, try PostGIS query for optimal performance
    const radiusMeters = radiusMiles * 1609.34; // Convert miles to meters

    // Use PostGIS ST_DWithin for efficient spatial query
    const { data, error } = await supabase
      .from('Riverside_County_Station_List_Public')
      .select('*')
      .not('Latitude', 'is', null)
      .not('Longitude', 'is', null);

    if (error) {
      console.error('Error fetching competitors:', error);
      return [];
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Client-side distance filtering (fallback if PostGIS query doesn't work)
    const competitorsWithDistance = data
      .map((station: any): CompetitorWithDistance | null => {
        const stationLat = station.Latitude;
        const stationLon = station.Longitude;

        if (!stationLat || !stationLon) return null;

        const distance = calculateDistance(latitude, longitude, stationLat, stationLon);

        return {
          ...station,
          distance_miles: distance
        } as CompetitorWithDistance;
      })
      .filter((station: CompetitorWithDistance | null): station is CompetitorWithDistance =>
        station !== null &&
        (station.distance_miles ?? 999) <= radiusMiles &&
        station.OBJECTID !== excludeObjectId
      )
      .sort((a: CompetitorWithDistance, b: CompetitorWithDistance) => (a.distance_miles ?? 0) - (b.distance_miles ?? 0));

    return competitorsWithDistance;
  } catch (error) {
    console.error('Error in fetchCompetitorsInRadius:', error);
    return [];
  }
}

/**
 * Fetch a single station by OBJECTID (used for subject property)
 */
export async function fetchStationByObjectId(
  objectId: number
): Promise<AddressRecord | null> {
  try {
    const { data, error } = await supabase
      .from('Riverside_County_Station_List_Public')
      .select('*')
      .eq('OBJECTID', objectId)
      .single();

    if (error) {
      console.error('Error fetching station:', error);
      return null;
    }

    return data as AddressRecord;
  } catch (error) {
    console.error('Error in fetchStationByObjectId:', error);
    return null;
  }
}

/**
 * Fetch a single station by address (fallback when OBJECTID is not available)
 * Normalizes address for matching
 */
export async function fetchStationByAddress(
  address: string
): Promise<AddressRecord | null> {
  try {
    // Normalize the address for matching
    // Remove extra spaces, convert to uppercase
    const normalizedAddress = address.trim().toUpperCase();

    // Extract components from address
    // Format: "1155 N GROVE AVE, ONTARIO, CALIFORNIA 91764"
    const parts = normalizedAddress.split(',').map(p => p.trim());
    const streetAddress = parts[0]; // "1155 N GROVE AVE"
    const city = parts[1]; // "ONTARIO"
    const zipMatch = normalizedAddress.match(/\b\d{5}\b/); // Extract ZIP
    const zipCode = zipMatch ? zipMatch[0] : '';

    // Try exact match first
    let query = supabase
      .from('Riverside_County_Station_List_Public')
      .select('*');

    if (city) {
      query = query.ilike('City', city);
    }

    if (zipCode) {
      query = query.eq('ZIP_Code', zipCode);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching station by address:', error);
      return null;
    }

    if (!data || data.length === 0) {
      return null;
    }

    // If we have multiple results, try to match the street address
    if (data.length === 1) {
      return data[0] as AddressRecord;
    }

    // Try to find exact street address match
    const exactMatch = data.find((station: any) => {
      const stationAddress = station.Facility_Address?.toUpperCase().trim();
      return stationAddress === streetAddress;
    });

    if (exactMatch) {
      return exactMatch as AddressRecord;
    }

    // Return first match if no exact street address match
    return data[0] as AddressRecord;
  } catch (error) {
    console.error('Error in fetchStationByAddress:', error);
    return null;
  }
}

/**
 * Get competitor summary statistics
 */
export function getCompetitorSummary(competitors: CompetitorWithDistance[]) {
  const total = competitors.length;

  const branded = competitors.filter(c =>
    c.Fuel_Brand && c.Fuel_Brand.trim() !== '' && c.Fuel_Brand.toLowerCase() !== 'unbranded'
  ).length;

  const unbranded = total - branded;

  const avgDistance = competitors.length > 0
    ? competitors.reduce((sum, c) => sum + (c.distance_miles ?? 0), 0) / competitors.length
    : 0;

  const avgBuildingSize = competitors
    .filter(c => c.Cstore_Size_Measured && c.Cstore_Size_Measured > 0)
    .reduce((sum, c, _, arr) => sum + (c.Cstore_Size_Measured ?? 0) / arr.length, 0);

  const avgCanopySize = competitors
    .filter(c => c.Fueling_Canopy_Size_Measured && c.Fueling_Canopy_Size_Measured > 0)
    .reduce((sum, c, _, arr) => sum + (c.Fueling_Canopy_Size_Measured ?? 0) / arr.length, 0);

  const brands = competitors
    .map(c => c.Fuel_Brand)
    .filter((brand): brand is string => !!brand && brand.trim() !== '')
    .reduce((acc, brand) => {
      acc[brand] = (acc[brand] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

  return {
    total,
    branded,
    unbranded,
    avgDistance: Math.round(avgDistance * 10) / 10,
    avgBuildingSize: Math.round(avgBuildingSize),
    avgCanopySize: Math.round(avgCanopySize),
    brandBreakdown: brands,
    closestCompetitor: competitors[0] || null
  };
}
